// src/pages/ProjectDetailPage.js

import React, { useContext, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { projectService } from '../api';
import useApi from '../hooks/useApi';
import SocialShare from '../components/common/SocialShare';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';

// Styled components
const ProjectSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectContent = styled.div`
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const ProjectTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ProjectStatus = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;
  background-color: ${props => {
    switch (props.status) {
      case 'planning':
        return props.theme.accent;
      case 'in_progress':
        return props.theme.primary;
      case 'completed':
        return props.theme.success;
      case 'on_hold':
        return '#f59e0b';
      default:
        return props.theme.text;
    }
  }};
  margin-left: 1rem;
  vertical-align: middle;
`;

const ProjectMetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 1.5rem 0;
  color: ${props => props.theme.text};
  
  div {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 0.5rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const ProjectDescription = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.8;
  margin-bottom: 2rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: ${props => props.theme.primary};
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 1.8rem 0 1rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const TechTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  background: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const ProjectSidebar = styled.div`
`;

const SidebarCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const SidebarList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    padding: 0.8rem 0;
    border-bottom: 1px solid ${props => props.theme.text}10;
    color: ${props => props.theme.text};
    display: flex;
    justify-content: space-between;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const TeamMember = styled.div`
  text-align: center;
`;

const MemberImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: cover;
  background-position: center;
  margin: 0 auto 0.5rem;
  border: 2px solid ${props => props.theme.primary};
`;

const MemberName = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ProjectDetailPage = () => {
  const { projectSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  // Charger les détails du projet
  const { 
    data: project, 
    loading: projectLoading, 
    error: projectError 
  } = useApi(() => projectService.getById(projectSlug), [projectSlug]);
  
  // Mise à jour du titre de la page
  useEffect(() => {
    if (project) {
      document.title = `${project.title} | PropentaTech`;
    }
    return () => {
      document.title = 'PropentaTech';
    };
  }, [project]);
  
  // Traduction du statut du projet
  const getStatusLabel = (status) => {
    switch (status) {
      case 'planning':
        return t('planning');
      case 'in_progress':
        return t('inProgress');
      case 'completed':
        return t('completed');
      case 'on_hold':
        return t('onHold');
      default:
        return status;
    }
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return t('ongoing');
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  if (projectLoading) {
    return (
      <ProjectSection>
        <Container>
          <LoadingIndicator>
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle 
                cx="25" 
                cy="25" 
                r="20" 
                fill="none" 
                stroke={theme.primary} 
                strokeWidth="3" 
                strokeLinecap="round"
              >
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  from="0 25 25" 
                  to="360 25 25" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dasharray" 
                  from="1, 150" 
                  to="90, 150" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dashoffset" 
                  from="0" 
                  to="-35" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </LoadingIndicator>
        </Container>
      </ProjectSection>
    );
  }
  
  if (projectError) {
    return (
      <ProjectSection>
        <Container>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>{projectError}</h2>
              <Button variant="primary" to="/projects">
                <i className="fas fa-arrow-left"></i> {t('backToProjects')}
              </Button>
            </motion.div>
          </div>
        </Container>
      </ProjectSection>
    );
  }
  
  if (!project) return null;
  
  // Extraire les technologies du projet
  const technologies = project.technologies ? project.technologies.split(',').map(tech => tech.trim()) : [];
  
  return (
    <ProjectSection>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs theme={theme}>
            <Link to="/">{t('home')}</Link> <span>/</span> 
            <Link to="/projects">{t('projects')}</Link> <span>/</span> 
            <span>{project.title}</span>
          </Breadcrumbs>
          
          <ProjectTitle theme={theme}>
            {project.title}
            <ProjectStatus theme={theme} status={project.status}>
              {getStatusLabel(project.status)}
            </ProjectStatus>
          </ProjectTitle>
          
          <ProjectGrid>
            <ProjectContent>
              {project.image && (
                <ProjectImage src={project.image} />
              )}
              
              <ProjectMetaInfo theme={theme}>
                <div>
                  <i className="far fa-calendar-alt"></i>
                  <span>{t('startDate')}: {formatDate(project.start_date)}</span>
                </div>
                {project.end_date && (
                  <div>
                    <i className="far fa-calendar-check"></i>
                    <span>{t('endDate')}: {formatDate(project.end_date)}</span>
                  </div>
                )}
                {project.client && (
                  <div>
                    <i className="fas fa-building"></i>
                    <span>{t('client')}: {project.client}</span>
                  </div>
                )}
              </ProjectMetaInfo>
              
              <SocialShare 
                title={project.title}
                hashtags={['PropentaTech', 'Project', project.title.replace(/\s+/g, '')]}
              />
              
              <ProjectDescription theme={theme} dangerouslySetInnerHTML={{ __html: project.description }} />
              
              {technologies.length > 0 && (
                <>
                  <h2 style={{ color: theme.primary, marginBottom: '1rem' }}>{t('technologiesUsed')}</h2>
                  <TechTagsContainer>
                    {technologies.map((tech, index) => (
                      <TechTag key={index} theme={theme}>{tech}</TechTag>
                    ))}
                  </TechTagsContainer>
                </>
              )}
            </ProjectContent>
            
            <ProjectSidebar>
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('projectDetails')}</SidebarTitle>
                <SidebarList theme={theme}>
                  <li>
                    <strong>{t('status')}</strong>
                    <span>{getStatusLabel(project.status)}</span>
                  </li>
                  <li>
                    <strong>{t('startDate')}</strong>
                    <span>{formatDate(project.start_date)}</span>
                  </li>
                  {project.end_date && (
                    <li>
                      <strong>{t('endDate')}</strong>
                      <span>{formatDate(project.end_date)}</span>
                    </li>
                  )}
                  {project.client && (
                    <li>
                      <strong>{t('client')}</strong>
                      <span>{project.client}</span>
                    </li>
                  )}
                </SidebarList>
              </SidebarCard>
              
              {project.members && project.members.length > 0 && (
                <SidebarCard theme={theme}>
                  <SidebarTitle theme={theme}>{t('projectTeam')}</SidebarTitle>
                  <TeamGrid>
                    {project.members.map(member => (
                      <TeamMember key={member.id}>
                        <MemberImage src={member.profile_picture} theme={theme} />
                        <MemberName theme={theme}>
                          {member.first_name} {member.last_name}
                        </MemberName>
                      </TeamMember>
                    ))}
                  </TeamGrid>
                </SidebarCard>
              )}
              
              {project.is_open_for_applications && (
                <SidebarCard theme={theme}>
                  <SidebarTitle theme={theme}>{t('joinProject')}</SidebarTitle>
                  <p style={{ color: theme.text, marginBottom: '1.5rem' }}>
                    {t('joinProjectDescription')}
                  </p>
                  <Button
                    variant="primary"
                    size="large"
                    style={{ width: '100%' }}
                    to={`/projects/${project.id}/apply`}
                  >
                    <i className="fas fa-paper-plane" style={{ marginRight: '8px' }}></i>
                    {t('applyNow')}
                  </Button>
                </SidebarCard>
              )}
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('contactUs')}</SidebarTitle>
                <p style={{ color: theme.text, marginBottom: '1.5rem' }}>
                  {t('projectContactDescription')}
                </p>
                <Button
                  variant="outline"
                  size="large"
                  style={{ width: '100%' }}
                  to="/contact"
                >
                  <i className="fas fa-envelope" style={{ marginRight: '8px' }}></i>
                  {t('getInTouch')}
                </Button>
              </SidebarCard>
            </ProjectSidebar>
          </ProjectGrid>
        </motion.div>
      </Container>
    </ProjectSection>
  );
};

export default ProjectDetailPage;